import React, { useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import clsx from 'clsx';
import Icon from '../Icon';
import { post } from '../../Services/http';
import { triggerToast } from '../../Services/Toast.service';
import LoadingSpinner from '../LoadingSpinner';
import { PRICE_TOGGLE_BEFORE_RELOAD_EVENT } from '../../constants';

interface PriceToggleProps {
  showRecommendedPrice: boolean;
  canChange: boolean;
}

type SelectedPriceValue = 'recommended' | 'purchase';

const options: { label: string; value: SelectedPriceValue }[] = [
  {
    label: translate('price-toggle.recommended-price'),
    value: 'recommended',
  },
  {
    label: translate('price-toggle.purchase-price'),
    value: 'purchase',
  },
];

const PriceToggle = ({
  showRecommendedPrice,
  canChange,
}: PriceToggleProps) => {
  const dispatch = useTypedDispatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clickedValue, setClickedValue] = useState<
    SelectedPriceValue | undefined
  >();
  const [initialValue] = useState<SelectedPriceValue>(
    showRecommendedPrice ? 'recommended' : 'purchase'
  );

  useEffect(() => {
    dropdownOpen
      ? dispatch(
          addOverlay('price-toggle', () => setDropdownOpen(false), '')
        )
      : dispatch(removeOverlay('price-toggle'));
  }, [dropdownOpen]);

  const submitPriceToggle = async (value: SelectedPriceValue) => {
    if (!canChange) return;

    try {
      setIsLoading(true);
      await post(`/api/pricetoggle/${value}`, {});
      window.dispatchEvent(new Event(PRICE_TOGGLE_BEFORE_RELOAD_EVENT));
      window.location.reload();
    } catch (err) {
      triggerToast({
        title: translate('general.error'),
        text: translate('price-toggle.general-error'),
        type: 'error',
        autoClose: false
      });
      console.error(err);
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => canChange && setDropdownOpen(!dropdownOpen)}
        className="block text-md font-light text-white"
        disabled={!canChange}
      >
        {showRecommendedPrice
          ? translate('price-toggle.showing-recommended')
          : translate('price-toggle.showing-purchase')}
        {canChange && (
          <Icon
            name="chevron-down"
            className={clsx(
              'ml-1 transition-transform ease-in-out',
              dropdownOpen ? 'rotate-180' : 'rotate-0'
            )}
          />
        )}
      </button>
      {dropdownOpen && (
        <ul className="absolute right-0 top-[25px] z-[100] min-w-[200px] overflow-hidden rounded border border-gray-300 bg-white">
          {options.map((opt) => (
            <li
              className={clsx(
                'border-b border-gray-300 px-3 py-2.5 text-md',
                opt.value == initialValue && 'cursor-default font-medium'
              )}
              key={opt.value}
            >
              <button
                onClick={() => {
                  submitPriceToggle(opt.value);
                  setClickedValue(opt.value);
                }}
                disabled={initialValue == opt.value || isLoading}
                className={clsx(isLoading && 'text-gray-300')}
              >
                {opt.label}
                {clickedValue == opt.value && isLoading && (
                  <LoadingSpinner className="ml-2" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PriceToggle;
